import { SvgIcon } from '@cinch-labs/design-system'
import { Table as DsTable } from '../../design-system/table'
import type { TableRows } from '../../design-system/table'
import styles from './table.module.css'

export interface StoryblokTableCell {
  _uid: string
  value: string
  component: '_table_head' | '_table_col'
}

export interface StoryblokTableRow {
  _uid: string
  body: StoryblokTableCell[]
  component: '_tablerow'
}

export interface TableProps {
  fieldtype: 'table'
  thead: StoryblokTableCell[]
  tbody: StoryblokTableRow[]
}

function TableStatusIcon({
  name,
  variant,
}: {
  name: 'tick' | 'cross'
  variant: 'yes' | 'no'
}) {
  return (
    <span
      className={variant === 'no' ? styles.statusIconNo : styles.statusIconYes}
    >
      <SvgIcon
        name={name}
        size={16}
        color={variant === 'no' ? 'deepPurple' : 'pureWhite'}
        aria-hidden="true"
      />
    </span>
  )
}

export function parseTableCellContent(content: string): React.ReactNode {
  if (content.includes('[YES]')) {
    return <TableStatusIcon name="tick" variant="yes" />
  }
  if (content.includes('[NO]')) {
    return <TableStatusIcon name="cross" variant="no" />
  }
  return content
}

export const Table = ({ thead, tbody }: TableProps) => {
  const headRows: TableRows = []
  const bodyRows: TableRows = []

  if (thead.length > 0) {
    const headerCells = thead.map((cell, cellIndex) => ({
      variant: 'dark-2' as const,
      align: cellIndex === 0 ? ('left' as const) : ('center' as const),
      children: parseTableCellContent(cell.value),
    }))

    headRows.push({ cells: headerCells })
  }

  tbody.forEach((row) => {
    if (row.body && row.body.length > 0) {
      const bodyCells = row.body.map((cell, cellIndex) => {
        const variant: 'light-2' | 'light-1' =
          cellIndex === 0 ? 'light-2' : 'light-1'
        const align = cellIndex === 0 ? ('left' as const) : ('center' as const)

        return {
          variant,
          align,
          children: parseTableCellContent(cell.value),
        }
      })

      bodyRows.push({ cells: bodyCells })
    }
  })

  return (
    <DsTable headRows={headRows} bodyRows={bodyRows} overflow="fit-parent" />
  )
}
